import React, { useEffect, useMemo, useState } from 'react';
import { getForecastProjection, upsertCellOverride, deleteCellOverride, bulkUpsertCellOverrides } from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ArrowsClockwise, CalendarBlank, TrendUp, TrendDown, Warning, PencilSimple, X, Selection } from '@phosphor-icons/react';
import { toast } from 'sonner';
import OverrideAuditFeed, { OverrideAuditTrigger } from './OverrideAuditFeed';


/**
 * Master Planner Calendar — aggregates the weekly projection into a grid
 * view (monthly or weekly). Each cell shows inbound (+), demand (−), and a
 * risk heatmap tint based on aggregate closing stock across all SKUs.
 */
export default function PlannerCalendar() {
  const [mode, setMode] = useState('month'); // 'month' | 'week' | 'matrix'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);

  const horizon = mode === 'week' ? 8 : mode === 'matrix' ? 12 : 12;

  const load = async () => {
    setLoading(true);
    try {
      const res = await getForecastProjection({ horizon_weeks: horizon, limit: 500 });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode]);

  // Aggregate each week across all SKUs
  const weekCells = useMemo(() => {
    if (!data?.rows?.length) return [];
    const weeks = data.rows[0].weeks.map((_, i) => ({
      week_index: i,
      week_start: data.rows[0].weeks[i].week_start,
      total_inbound: 0,
      total_demand: 0,
      total_closing: 0,
      stockout_skus: 0,
      low_skus: 0,
      active_skus: 0,
    }));
    for (const row of data.rows) {
      row.weeks.forEach((w, i) => {
        weeks[i].total_inbound += w.inbound;
        weeks[i].total_demand += w.demand;
        weeks[i].total_closing += w.closing;
        if (w.closing <= 0) weeks[i].stockout_skus += 1;
        else if (w.closing < (row.current_stock || 1) * 0.25) weeks[i].low_skus += 1;
        if (w.inbound > 0 || w.demand > 0) weeks[i].active_skus += 1;
      });
    }
    return weeks.map(w => ({
      ...w,
      total_inbound: Math.round(w.total_inbound),
      total_demand: Math.round(w.total_demand),
      total_closing: Math.round(w.total_closing),
    }));
  }, [data]);

  const monthly = useMemo(() => {
    if (mode !== 'month' || !weekCells.length) return [];
    const buckets = {};
    for (const cell of weekCells) {
      const d = new Date(cell.week_start);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      if (!buckets[key]) {
        buckets[key] = {
          month_key: key,
          label: d.toLocaleString('default', { month: 'short', year: 'numeric', timeZone: 'UTC' }),
          total_inbound: 0,
          total_demand: 0,
          stockout_skus: 0,
          low_skus: 0,
          weeks: [],
        };
      }
      buckets[key].total_inbound += cell.total_inbound;
      buckets[key].total_demand += cell.total_demand;
      buckets[key].stockout_skus = Math.max(buckets[key].stockout_skus, cell.stockout_skus);
      buckets[key].low_skus = Math.max(buckets[key].low_skus, cell.low_skus);
      buckets[key].weeks.push(cell);
    }
    return Object.values(buckets);
  }, [mode, weekCells]);

  const riskTone = (stockout, low) => {
    if (stockout > 0) return 'border-red-500/40 bg-red-500/5';
    if (low > 0) return 'border-amber-500/40 bg-amber-500/5';
    return 'border-emerald-500/30 bg-emerald-500/5';
  };

  if (loading) return <div className="text-sm opacity-60" data-testid="calendar-loading">Loading calendar…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="planner-calendar">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-muted rounded-md p-0.5">
          <button
            data-testid="calendar-mode-month"
            onClick={() => setMode('month')}
            className={`px-3 py-1.5 text-[12px] rounded ${mode === 'month' ? 'bg-background font-bold shadow-sm' : 'opacity-60'}`}
          >Monthly</button>
          <button
            data-testid="calendar-mode-week"
            onClick={() => setMode('week')}
            className={`px-3 py-1.5 text-[12px] rounded ${mode === 'week' ? 'bg-background font-bold shadow-sm' : 'opacity-60'}`}
          >Weekly</button>
          <button
            data-testid="calendar-mode-matrix"
            onClick={() => setMode('matrix')}
            className={`px-3 py-1.5 text-[12px] rounded ${mode === 'matrix' ? 'bg-background font-bold shadow-sm' : 'opacity-60'}`}
          >Matrix</button>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="calendar-refresh">
          <ArrowsClockwise className="size-3.5 mr-1" /> Refresh
        </Button>
        <OverrideAuditTrigger onClick={() => setAuditOpen(true)} />
        <div className="ml-auto text-[11px] opacity-60 font-mono">
          {data.count} SKUs · {horizon}w horizon
        </div>
      </div>
      <OverrideAuditFeed open={auditOpen} onOpenChange={setAuditOpen} />

      {mode === 'matrix' ? (
        <MatrixView data={data} onCellEdit={load} />
      ) : mode === 'week' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {weekCells.map((w) => (
            <Card key={w.week_index}
                  className={`p-4 border-2 ${riskTone(w.stockout_skus, w.low_skus)}`}
                  data-testid={`calendar-week-${w.week_index}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">
                  Week {w.week_index + 1}
                </div>
                <CalendarBlank className="size-4 opacity-40" />
              </div>
              <div className="text-[13px] font-bold mt-0.5 font-mono">{w.week_start}</div>
              <div className="mt-3 space-y-1 text-[11px] font-mono">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <TrendUp className="size-3" /> inbound
                  </span>
                  <span className="font-bold">+{w.total_inbound}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-red-600">
                    <TrendDown className="size-3" /> demand
                  </span>
                  <span className="font-bold">−{w.total_demand}</span>
                </div>
                {(w.stockout_skus > 0 || w.low_skus > 0) && (
                  <div className="flex items-center gap-1 pt-1.5 mt-1.5 border-t border-border/40">
                    <Warning className="size-3 text-amber-600" />
                    <span className="opacity-80">
                      {w.stockout_skus > 0 && `${w.stockout_skus} stockout`}
                      {w.stockout_skus > 0 && w.low_skus > 0 && ' · '}
                      {w.low_skus > 0 && `${w.low_skus} low`}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {monthly.map((m) => (
            <Card key={m.month_key} className="p-4" data-testid={`calendar-month-${m.month_key}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Month</div>
                  <h3 className="text-xl font-bold tracking-tight">
                    {m.label}
                  </h3>
                </div>
                <div className="flex gap-4 text-[11px] font-mono">
                  <div className="text-emerald-600">+{m.total_inbound} in</div>
                  <div className="text-red-600">−{m.total_demand} out</div>
                  {(m.stockout_skus > 0 || m.low_skus > 0) && (
                    <div className="text-amber-600 flex items-center gap-1">
                      <Warning className="size-3" />
                      {m.stockout_skus} stockout · {m.low_skus} low
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {m.weeks.map((w) => (
                  <div key={w.week_index}
                       className={`p-2.5 border rounded ${riskTone(w.stockout_skus, w.low_skus)}`}>
                    <div className="text-[10px] font-mono opacity-70">
                      Wk {w.week_index + 1} · {w.week_start}
                    </div>
                    <div className="flex justify-between text-[11px] font-mono mt-1">
                      <span className="text-emerald-600">+{w.total_inbound}</span>
                      <span className="text-red-600">−{w.total_demand}</span>
                    </div>
                    {(w.stockout_skus > 0 || w.low_skus > 0) && (
                      <div className="text-[10px] text-amber-600 mt-1">
                        {w.stockout_skus > 0 && `${w.stockout_skus} out`}
                        {w.stockout_skus > 0 && w.low_skus > 0 && ' · '}
                        {w.low_skus > 0 && `${w.low_skus} low`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="text-[11px] opacity-60 font-mono">
        Risk tint: <span className="text-red-600">red = projected stockout</span> ·
        <span className="text-amber-600 ml-1">amber = low-cover</span> ·
        <span className="text-emerald-600 ml-1">green = healthy</span>
      </div>
    </div>
  );
}

/**
/**
 * Spreadsheet-matrix view — SKU rows × week columns with sticky first column
 * and sticky header row. Lightweight viewport-clipped virtualization keeps
 * render cost bounded for catalogs of any size: only rows whose y-range
 * intersects the visible viewport (+ overscan) are mounted.
 */
const ROW_HEIGHT = 44;     // matches table-dense default
const OVERSCAN = 8;

function MatrixView({ data, onCellEdit }) {
  const rows = data?.rows || [];
  const weeks = rows[0]?.weeks || [];
  const scrollRef = React.useRef(null);
  const [viewport, setViewport] = React.useState({ top: 0, height: 0 });
  const [editing, setEditing] = React.useState(null); // {sku_id, week, field, value, sku_code}
  const [saving, setSaving] = React.useState(false);
  // Bulk-select state — array of {sku_id, sku_code, week, field}
  const [selection, setSelection] = React.useState([]);
  const [bulk, setBulk] = React.useState(null); // {open, mode, value}
  const [lastClicked, setLastClicked] = React.useState(null); // {rowIdx, weekIdx, field}

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setViewport({ top: el.scrollTop, height: el.clientHeight });
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => setViewport({ top: el.scrollTop, height: el.clientHeight });
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [rows.length]);

  const tone = (closing, demand) => {
    if (closing <= 0) return 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-200';
    if (demand > 0 && closing <= demand) return 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200';
    return 'bg-emerald-50/60 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300';
  };

  const startEdit = (e, row, weekIdx, field) => {
    e.stopPropagation();
    // Shift-click toggles selection; plain click opens single-cell editor
    if (e.shiftKey) {
      e.preventDefault();
      const rowIdx = rows.indexOf(row);
      // If we have a previous click + same field, select the rectangular range.
      if (lastClicked && lastClicked.field === field) {
        const r1 = Math.min(rowIdx, lastClicked.rowIdx);
        const r2 = Math.max(rowIdx, lastClicked.rowIdx);
        const w1 = Math.min(weekIdx, lastClicked.weekIdx);
        const w2 = Math.max(weekIdx, lastClicked.weekIdx);
        const next = [];
        for (let r = r1; r <= r2; r++) {
          for (let w = w1; w <= w2; w++) {
            next.push({
              sku_id: rows[r].sku_id,
              sku_code: rows[r].sku_code,
              week: w,
              field,
            });
          }
        }
        setSelection(next);
      } else {
        const cell = { sku_id: row.sku_id, sku_code: row.sku_code, week: weekIdx, field };
        setSelection((prev) => {
          const exists = prev.find((c) => c.sku_id === cell.sku_id && c.week === cell.week && c.field === cell.field);
          return exists
            ? prev.filter((c) => !(c.sku_id === cell.sku_id && c.week === cell.week && c.field === cell.field))
            : [...prev, cell];
        });
        setLastClicked({ rowIdx, weekIdx, field });
      }
      return;
    }
    // Plain click: open single-cell editor
    setSelection([]);
    setLastClicked({ rowIdx: rows.indexOf(row), weekIdx, field });
    const w = row.weeks[weekIdx];
    setEditing({
      sku_id: row.sku_id,
      sku_code: row.sku_code,
      week: weekIdx,
      field,
      value: String(w[field]),
      isOverride: !!w[`${field}_overridden`],
    });
  };

  const isSelected = (skuId, week, field) =>
    selection.some((c) => c.sku_id === skuId && c.week === week && c.field === field);

  const applyBulk = async () => {
    if (!bulk || !selection.length) return;
    const num = parseFloat(bulk.value);
    if (isNaN(num) || (bulk.mode === 'absolute' && num < 0)) {
      toast.error(bulk.mode === 'absolute' ? 'Enter a non-negative number' : 'Enter a numeric uplift %');
      return;
    }
    setSaving(true);
    try {
      // For percent mode, attach `base` from the projected value the planner
      // is currently looking at — backend uses it as the uplift base.
      const cells = selection.map((c) => {
        const out = { sku_id: c.sku_id, week_offset: c.week, field: c.field };
        if (bulk.mode === 'percent') {
          const row = rows.find((r) => r.sku_id === c.sku_id);
          if (row && row.weeks[c.week]) {
            out.base = Number(row.weeks[c.week][c.field]) || 0;
          }
        }
        return out;
      });
      const res = await bulkUpsertCellOverrides({ cells, mode: bulk.mode, value: num });
      const { written, skipped, errors } = res.data;
      if (written > 0) {
        toast.success(`Bulk override applied · ${written} cell${written === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped)` : ''}`);
      } else if (skipped > 0) {
        toast.error(errors?.[0] || 'No cells were written');
      }
      setBulk(null);
      setSelection([]);
      onCellEdit?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const num = parseFloat(editing.value);
    if (isNaN(num) || num < 0) {
      toast.error('Enter a non-negative number');
      return;
    }
    setSaving(true);
    try {
      await upsertCellOverride({
        sku_id: editing.sku_id,
        week_offset: editing.week,
        field: editing.field,
        value: num,
      });
      toast.success(`Override saved · ${editing.sku_code} W${editing.week + 1} ${editing.field}`);
      setEditing(null);
      onCellEdit?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save override');
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await deleteCellOverride(editing.sku_id, editing.week, editing.field);
      toast.success('Override cleared — back to forecast');
      setEditing(null);
      onCellEdit?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear override');
    } finally {
      setSaving(false);
    }
  };

  if (!rows.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground text-sm">
        No SKU projections to render.
      </div>
    );
  }

  // Header is ~36px (sticky); subtract before computing visible row range.
  const HEADER_HEIGHT = 36;
  const usableHeight = Math.max(0, viewport.height - HEADER_HEIGHT);
  const startIndex = Math.max(0, Math.floor((viewport.top - HEADER_HEIGHT) / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(rows.length, Math.ceil((viewport.top - HEADER_HEIGHT + usableHeight) / ROW_HEIGHT) + OVERSCAN);
  const padTop = startIndex * ROW_HEIGHT;
  const padBottom = Math.max(0, (rows.length - endIndex) * ROW_HEIGHT);
  const visibleRows = rows.slice(startIndex, endIndex);

  return (
    <>
    <div
      ref={scrollRef}
      className="bg-card border border-border rounded-lg overflow-auto relative"
      style={{ maxHeight: 'calc(100vh - 320px)' }}
      data-testid="calendar-matrix"
      data-virtualized="true"
      data-row-count={rows.length}
      data-rendered-count={visibleRows.length}
    >
      <table className="w-full text-[12px] border-collapse"><thead><tr>
            <th
              className="sticky top-0 left-0 z-30 bg-secondary text-muted-foreground text-[10px] font-semibold uppercase tracking-wider px-3 py-2 text-left border-b border-r border-border min-w-[220px]"
              style={{ position: 'sticky' }}
            >
              SKU
            </th>
            {weeks.map((w, i) => (
              <th
                key={i}
                className="sticky top-0 z-20 bg-secondary text-muted-foreground text-[10px] font-semibold uppercase tracking-wider px-2 py-2 text-center border-b border-r border-border min-w-[88px]"
              >
                <div className="font-mono">W{i + 1}</div>
                <div className="text-[9px] opacity-60 font-mono">{w.week_start?.slice(5)}</div>
              </th>
            ))}
          </tr></thead><tbody>{padTop > 0 && (
            <tr style={{ height: padTop }} aria-hidden="true"><td colSpan={weeks.length + 1} /></tr>
          )}{visibleRows.map((row) => (
            <tr
              key={row.sku_id || row.sku_code}
              data-testid={`matrix-row-${row.sku_code}`}
              style={{ height: ROW_HEIGHT }}
            >
              <td
                className="sticky left-0 z-10 bg-card text-foreground border-b border-r border-border px-3 py-1.5 text-left"
                style={{ position: 'sticky' }}
              >
                <div className="font-mono text-[11px] font-semibold">{row.sku_code}</div>
                <div className="text-[10px] text-muted-foreground truncate max-w-[200px]">{row.sku_name}</div>
              </td>
              {row.weeks.map((w, i) => {
                const isDemandOvr = !!w.demand_overridden;
                const isInboundOvr = !!w.inbound_overridden;
                return (
                  <td
                    key={i}
                    className={`relative group border-b border-r border-border px-1 py-1 text-center align-middle ${tone(w.closing, w.demand)}`}
                    title={`Closing ${w.closing} · +${w.inbound} in · −${w.demand} out${isDemandOvr || isInboundOvr ? ' · edited' : ''}`}
                    data-testid={`matrix-cell-${row.sku_code}-w${i}`}
                  >
                    <div className="font-mono font-semibold flex items-center justify-center gap-1">
                      {(isDemandOvr || isInboundOvr) && <span className="w-1 h-1 rounded-full bg-brand" aria-label="overridden" />}
                      {w.closing}
                    </div>
                    <div className="text-[9px] opacity-70 font-mono flex items-center justify-center gap-1">
                      {w.inbound > 0 && (
                        <button
                          type="button"
                          onClick={(e) => startEdit(e, row, i, 'inbound')}
                          data-testid={`matrix-edit-inbound-${row.sku_code}-w${i}`}
                          data-selected={isSelected(row.sku_id, i, 'inbound') ? 'true' : 'false'}
                          className={`hover:underline ${isInboundOvr ? 'font-semibold underline decoration-dotted' : ''} ${isSelected(row.sku_id, i, 'inbound') ? 'ring-2 ring-brand ring-offset-1 rounded px-0.5 bg-brand/10' : ''}`}
                          title="Click to override inbound · Shift-click to add to bulk selection"
                        >
                          +{w.inbound}
                        </button>
                      )}
                      {w.inbound > 0 && w.demand > 0 && ' '}
                      {w.demand > 0 && (
                        <button
                          type="button"
                          onClick={(e) => startEdit(e, row, i, 'demand')}
                          data-testid={`matrix-edit-demand-${row.sku_code}-w${i}`}
                          data-selected={isSelected(row.sku_id, i, 'demand') ? 'true' : 'false'}
                          className={`hover:underline ${isDemandOvr ? 'font-semibold underline decoration-dotted' : ''} ${isSelected(row.sku_id, i, 'demand') ? 'ring-2 ring-brand ring-offset-1 rounded px-0.5 bg-brand/10' : ''}`}
                          title="Click to override demand · Shift-click to add to bulk selection"
                        >
                          −{w.demand}
                        </button>
                      )}
                      {w.inbound === 0 && w.demand === 0 && (
                        <button
                          type="button"
                          onClick={(e) => startEdit(e, row, i, 'demand')}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Add demand override"
                          data-testid={`matrix-edit-empty-${row.sku_code}-w${i}`}
                        >
                          <PencilSimple size={9} />
                        </button>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}{padBottom > 0 && (
            <tr style={{ height: padBottom }} aria-hidden="true"><td colSpan={weeks.length + 1} /></tr>
          )}</tbody></table>
    </div>

    {/* Floating bulk-selection action bar */}
    {selection.length > 0 && !editing && !bulk && (
      <div
        data-testid="matrix-bulk-actionbar"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-card border border-border rounded-full shadow-lg px-4 py-2 flex items-center gap-3 backdrop-blur"
      >
        <Selection size={16} weight="bold" className="text-brand" />
        <span className="text-sm font-semibold text-foreground">
          {selection.length} cell{selection.length === 1 ? '' : 's'} selected
        </span>
        <span className="text-xs text-muted-foreground">
          ({Array.from(new Set(selection.map((s) => s.sku_code))).length} SKU{new Set(selection.map((s) => s.sku_code)).size === 1 ? '' : 's'} · {Array.from(new Set(selection.map((s) => s.field))).join(' + ')})
        </span>
        <div className="h-4 w-px bg-border mx-1" />
        <Button size="sm" variant="brand" onClick={() => setBulk({ mode: 'absolute', value: '' })} data-testid="matrix-bulk-set-value">
          Set value
        </Button>
        <Button size="sm" variant="outline" onClick={() => setBulk({ mode: 'percent', value: '' })} data-testid="matrix-bulk-uplift">
          Apply % uplift
        </Button>
        <button
          type="button"
          onClick={() => { setSelection([]); setLastClicked(null); }}
          className="text-muted-foreground hover:text-foreground ml-1"
          data-testid="matrix-bulk-clear"
          title="Clear selection (Esc)"
        >
          <X size={14} />
        </button>
      </div>
    )}

    {/* Bulk-edit modal */}
    {bulk && (
      <div
        data-testid="matrix-bulk-modal"
        className="fixed inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center z-50"
        onClick={() => !saving && setBulk(null)}
      >
        <div
          className="bg-card border border-border rounded-lg shadow-2xl p-5 w-full max-w-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Bulk override · {selection.length} cell{selection.length === 1 ? '' : 's'}
              </div>
              <div className="text-sm font-semibold text-foreground mt-0.5">
                {bulk.mode === 'absolute' ? 'Set every selected cell to this value' : 'Apply percentage uplift'}
              </div>
            </div>
            <button onClick={() => setBulk(null)} className="text-muted-foreground hover:text-foreground" data-testid="matrix-bulk-close">
              <X size={18} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {bulk.mode === 'absolute'
              ? 'Replaces the forecast value for each selected cell with the same number.'
              : 'Multiplies each cell\'s currently-projected value by (1 + N/100). Negative uplifts are allowed (−25% halves the lower quartile).'}
          </p>
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="number"
              step="0.01"
              value={bulk.value}
              placeholder={bulk.mode === 'absolute' ? '0' : '25'}
              onChange={(e) => setBulk({ ...bulk, value: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') applyBulk(); if (e.key === 'Escape') setBulk(null); }}
              data-testid="matrix-bulk-input"
              className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground font-mono focus:ring-2 focus:ring-ring focus:outline-none"
            />
            {bulk.mode === 'percent' && <span className="text-sm text-muted-foreground font-semibold">%</span>}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" size="sm" onClick={() => setBulk(null)} disabled={saving}>Cancel</Button>
            <Button type="button" size="sm" onClick={applyBulk} disabled={saving} data-testid="matrix-bulk-apply">
              {saving ? 'Applying…' : 'Apply & re-project'}
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Inline cell-edit popover */}
    {editing && (
      <div
        data-testid="matrix-cell-edit-popover"
        className="fixed inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center z-50"
        onClick={() => !saving && setEditing(null)}
      >
        <div
          className="bg-card border border-border rounded-lg shadow-2xl p-5 w-full max-w-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Override · W{editing.week + 1} · {editing.field}
              </div>
              <div className="text-sm font-mono font-semibold text-foreground mt-0.5">{editing.sku_code}</div>
            </div>
            <button
              onClick={() => setEditing(null)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="matrix-cell-edit-close"
            >
              <X size={18} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Replaces the forecast value for this single cell. Closing-stock downstream auto-recomputes on save.
          </p>
          <input
            autoFocus
            type="number"
            min="0"
            step="0.01"
            value={editing.value}
            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
            data-testid="matrix-cell-edit-input"
            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground font-mono focus:ring-2 focus:ring-ring focus:outline-none"
          />
          <div className="flex items-center justify-between gap-2 mt-4">
            {editing.isOverride ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearOverride}
                disabled={saving}
                data-testid="matrix-cell-edit-clear"
                className="text-destructive hover:bg-destructive/10"
              >
                Reset to forecast
              </Button>
            ) : <span />}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" size="sm" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={saveEdit}
                disabled={saving}
                data-testid="matrix-cell-edit-save"
              >
                {saving ? 'Saving…' : 'Save & re-project'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

